import { useCallback, useEffect, useMemo, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Search, UserCheck, X, User, AlertTriangle } from "lucide-react";
import Thumb from "@/components/Thumb";
import { useTheme } from "@/context/ThemeContext";

const STATUTS = {
  actif: { label: "Actif", cls: "border-emerald-500/40 text-emerald-600 dark:text-emerald-400" },
  expire_bientot: { label: "Expire bientôt", cls: "border-amber-500/40 text-amber-600 dark:text-amber-400" },
  expire: { label: "Expiré", cls: "border-red-500/40 text-red-600 dark:text-red-400" },
};

export default function Presences() {
  const { theme } = useTheme();
  const dark = theme === "dark";
  const [membres, setMembres] = useState([]);
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [m, p, s] = await Promise.all([api.get("/membres"), api.get("/presences"), api.get("/presences/stats")]);
      setMembres(m.data);
      setData(p.data);
      setStats(s.data);
    } catch {
      toast.error("Erreur de chargement");
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const dejaPointe = useMemo(() => new Set((data?.presences || []).map((p) => p.membre_id)), [data]);
  const resultats = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return membres.filter((m) => `${m.prenom} ${m.nom} ${m.telephone}`.toLowerCase().includes(q)).slice(0, 8);
  }, [search, membres]);

  const pointer = async (m, forcer = false) => {
    setBusy(m.id);
    try {
      await api.post("/presences", { membre_id: m.id, forcer });
      toast.success(`${m.prenom} ${m.nom} pointé(e) ✔`);
      setSearch("");
      refresh();
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error(errMsg(e), {
          action: { label: "Forcer", onClick: () => pointer(m, true) },
          duration: 8000,
        });
      } else {
        toast.error(errMsg(e));
      }
    } finally {
      setBusy(null);
    }
  };

  const annuler = async (p) => {
    try {
      await api.delete(`/presences/${p.id}`);
      toast.success("Pointage annulé");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!data || !stats) return <div className="text-muted-foreground text-sm animate-pulse" data-testid="presences-loading">Chargement…</div>;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="presences-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-foreground">Présences</h1>
        <p className="text-sm text-muted-foreground mt-1">Pointage rapide à l'entrée et fréquentation quotidienne</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 space-y-4">
          <Card className="bg-card border-border" data-testid="pointage-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-foreground flex items-center gap-2">
                <UserCheck className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /> Pointer un membre
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Nom, prénom ou téléphone…"
                  data-testid="pointage-search-input"
                  className="pl-9 bg-background border-border text-foreground h-11 text-base"
                />
              </div>
              {search && resultats.length === 0 && <p className="text-sm text-muted-foreground" data-testid="pointage-no-result">Aucun membre trouvé</p>}
              <div className="space-y-2">
                {resultats.map((m) => {
                  const done = dejaPointe.has(m.id);
                  return (
                    <div key={m.id} className="flex items-center gap-3 bg-background border border-border rounded-xl px-3 py-2.5" data-testid={`pointage-result-${m.id}`}>
                      <Thumb path={m.image_path} alt={m.nom} icon={User} className="w-10 h-10" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-foreground truncate">{m.prenom} {m.nom}</p>
                        <p className="text-xs text-muted-foreground">{m.telephone} · fin {new Date(`${m.date_fin}T12:00:00`).toLocaleDateString("fr-FR")}</p>
                      </div>
                      <Badge variant="outline" className={`${STATUTS[m.statut].cls} hidden sm:inline-flex`}>{STATUTS[m.statut].label}</Badge>
                      <Button
                        size="sm"
                        disabled={done || busy === m.id}
                        data-testid={`pointer-button-${m.id}`}
                        onClick={() => pointer(m)}
                        className={m.statut === "expire" ? "bg-red-500 hover:bg-red-600 text-white" : "bg-emerald-500 hover:bg-emerald-600 text-white"}
                      >
                        {done ? "Déjà pointé" : m.statut === "expire" ? <><AlertTriangle className="w-4 h-4 mr-1" /> Expiré</> : "Pointer"}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-4">
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Aujourd'hui</p>
                <p className="text-3xl font-display font-extrabold text-emerald-600 dark:text-emerald-400 mt-1" data-testid="presences-today-count">{stats.aujourdhui}</p>
                <p className="text-xs text-muted-foreground">passage(s)</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Ce mois</p>
                <p className="text-3xl font-display font-extrabold text-foreground mt-1" data-testid="presences-month-count">{stats.mois}</p>
                <p className="text-xs text-muted-foreground">passage(s)</p>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-card border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold text-foreground">Fréquentation — 14 derniers jours</CardTitle></CardHeader>
            <CardContent className="h-48">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={150}>
                <BarChart data={stats.jours}>
                  <CartesianGrid strokeDasharray="3 3" stroke={dark ? "#1E293B" : "#E2E8F0"} />
                  <XAxis dataKey="jour" stroke="#64748B" fontSize={11} />
                  <YAxis stroke="#64748B" fontSize={11} allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: dark ? "#131B2E" : "#fff", border: `1px solid ${dark ? "#1E293B" : "#E2E8F0"}`, borderRadius: 8 }} labelStyle={{ color: dark ? "#F8FAFC" : "#0F172A" }} />
                  <Bar dataKey="total" name="Présences" fill="#10B981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-card border-border lg:col-span-7" data-testid="presences-list-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-foreground flex items-center gap-2">
              Passages du jour
              <Badge variant="outline" className="border-border text-muted-foreground ml-auto">{data.total}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border">
                  <TableHead className="text-muted-foreground">Heure</TableHead>
                  <TableHead className="text-muted-foreground">Membre</TableHead>
                  <TableHead className="text-muted-foreground">Abonnement</TableHead>
                  <TableHead className="text-muted-foreground">Statut</TableHead>
                  <TableHead className="text-muted-foreground text-right">Annuler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.presences.length === 0 && (
                  <TableRow className="border-border"><TableCell colSpan={5} className="text-center text-muted-foreground py-8" data-testid="presences-empty">Aucun passage aujourd'hui</TableCell></TableRow>
                )}
                {data.presences.map((p) => (
                  <TableRow key={p.id} className="border-border" data-testid={`presence-row-${p.id}`}>
                    <TableCell className="font-mono text-sm text-foreground">{p.heure}</TableCell>
                    <TableCell className="font-medium text-foreground">{p.prenom} {p.nom}</TableCell>
                    <TableCell className="text-foreground/80 capitalize">{p.abonnement}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUTS[p.statut_membre]?.cls}>{STATUTS[p.statut_membre]?.label}{p.force ? " (forcé)" : ""}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-red-600" data-testid={`annuler-presence-${p.id}`} onClick={() => annuler(p)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
